import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useFetch } from '../../../utils/apiHelper';
import { ExternalAccountSyncStatus, IExternalAccountSyncDetail, IExternalAccountSyncRecord } from '../../../models/ExternalAccountSync/IExternalAccountSync';
import { getMockDetail } from '../ExternalAccountSyncMockData';

interface RecordDetailDrawerProps {
    open: boolean;
    stagingId: number | null;
    // When provided (demo mode), detail is derived locally instead of calling the API.
    demoRecord?: IExternalAccountSyncRecord | null;
    onClose: () => void;
}

const statusChipStyle: Record<ExternalAccountSyncStatus, { bg: string; color: string }> = {
    Pending: { bg: '#fff4e5', color: '#b26a00' },
    Approved: { bg: '#e3f2fd', color: '#0d47a1' },
    Processed: { bg: '#e8f5e9', color: '#2e7d32' },
    Rejected: { bg: '#fdecea', color: '#b71c1c' },
};

const money = (v?: number | null) => (v === null || v === undefined ? null : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// A single label/value row, left-aligned, dense — reads like a receipt line rather than a
// scattered form field. Rows with no value are visually muted rather than hidden, so the
// section still communicates "this field exists and is empty" instead of just disappearing.
const Row: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => {
    const empty = value === null || value === undefined || value === '';
    return (
        <Box
            display="flex"
            alignItems="baseline"
            justifyContent="space-between"
            sx={{
                py: '7px', px: '2px', gap: 2,
                '&:not(:last-of-type)': { borderBottom: '1px dashed #e9ebee' },
            }}
        >
            <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', flexShrink: 0 }}>
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: '0.86rem', fontWeight: empty ? 400 : 600, color: empty ? '#b7bcc4' : '#16233a',
                    textAlign: 'right', wordBreak: 'break-word',
                }}
            >
                {empty ? 'Not provided' : value}
            </Typography>
        </Box>
    );
};

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode; twoCol?: boolean }> = ({ icon, title, children, twoCol }) => (
    <Box
        sx={{
            borderRadius: 2, border: '1px solid #eceef1', backgroundColor: '#fff',
            padding: '14px 18px', mb: 2, boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
        }}
    >
        <Box display="flex" alignItems="center" gap={0.75} sx={{ mb: 1, color: '#007FFF', lineHeight: 0 }}>
            {icon}
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', lineHeight: 1, m: 0 }}>
                {title}
            </Typography>
        </Box>
        <Box
            sx={twoCol ? {
                display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 4,
                '& > *:not(:last-child):nth-of-type(odd)': { borderBottom: '1px dashed #e9ebee' },
            } : undefined}
        >
            {children}
        </Box>
    </Box>
);

const RecordDetailDrawer: React.FC<RecordDetailDrawerProps> = ({ open, stagingId, demoRecord, onClose }) => {
    const [detail, setDetail] = useState<IExternalAccountSyncDetail | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !stagingId) {
            setDetail(null);
            return;
        }

        if (demoRecord) {
            setDetail(getMockDetail(demoRecord));
            return;
        }

        setLoading(true);
        useFetch<IExternalAccountSyncDetail>(`ExternalAccountSyncDetailGet?StagingId=${stagingId}`)
            .then((response) => setDetail(response.data || null))
            .finally(() => setLoading(false));
    }, [open, stagingId, demoRecord]);

    if (!open) return null;

    const statusStyle = detail ? statusChipStyle[detail.status] : null;
    const title = detail?.businessName || `${detail?.firstName || ''} ${detail?.lastName || ''}`.trim() || 'Record Detail';

    return (
        <Box
            role="dialog"
            sx={{
                position: 'fixed', top: 0, right: 0, height: '100vh', width: 920, maxWidth: '96vw',
                backgroundColor: '#f5f6f8', boxShadow: '-8px 0 32px rgba(22,35,58,0.16)', zIndex: 1300,
                display: 'flex', flexDirection: 'column', fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
            }}
        >
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', backgroundColor: '#007FFF', color: '#fff', padding: '12px 44px 12px 20px', flexShrink: 0, gap: 1.25 }}>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2, m: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {title}
                </Typography>
                {detail && (
                    <>
                        <Typography sx={{ fontSize: '0.8rem', opacity: 0.85, lineHeight: 1, whiteSpace: 'nowrap' }}>
                            {detail.clientCode} · {detail.externalCustomerId}
                        </Typography>
                        {statusStyle && (
                            <Chip
                                size="small"
                                label={detail.status}
                                sx={{ backgroundColor: statusStyle.bg, color: statusStyle.color, fontWeight: 700, height: 20, flexShrink: 0 }}
                            />
                        )}
                    </>
                )}
                <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', color: 'white' }}>
                    <HighlightOffIcon style={{ fontSize: 24 }} />
                </IconButton>
            </Box>

            <Box flexGrow={1} sx={{ overflowY: 'auto', padding: '18px 22px' }}>
                {loading && <Typography variant="body2">Loading…</Typography>}
                {!loading && detail && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                            <Section icon={<BadgeOutlinedIcon sx={{ fontSize: 17 }} />} title="Identity" twoCol>
                                <Row label="Record Source" value={detail.recordSource} />
                                <Row label="Source System" value={detail.sourceSystemCode} />
                                <Row label="External Transaction ID" value={detail.externalTransactionId} />
                                <Row label="Dbt No" value={detail.dbtNo} />
                                <Row label="Phase" value={detail.phase} />
                                <Row label="Record Type" value={detail.recordType} />
                            </Section>
                        </Box>

                        <Section icon={<PersonOutlineOutlinedIcon sx={{ fontSize: 17 }} />} title="Demographic">
                            <Row label="First Name" value={detail.firstName} />
                            <Row label="Last Name" value={detail.lastName} />
                            <Row label="Business Name" value={detail.businessName} />
                            <Row label="Commercial" value={detail.commercial === 'Y' ? 'Yes' : detail.commercial === 'N' ? 'No' : detail.commercial} />
                        </Section>

                        <Section icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 17 }} />} title="Financial">
                            <Row label="Total Due" value={money(detail.totalDue)} />
                            <Row label="Amount" value={money(detail.amount)} />
                            <Row label="Transaction Date" value={detail.transactionDate} />
                        </Section>

                        <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                            <Section icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 17 }} />} title="Lifecycle" twoCol>
                                <Row label="Received Date" value={detail.receivedDate ? moment(detail.receivedDate).format('MM/DD/YYYY HH:mm') : ''} />
                                <Row label="Processed Date" value={detail.processedDate ? moment(detail.processedDate).format('MM/DD/YYYY HH:mm') : ''} />
                            </Section>
                        </Box>

                        <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                            <Accordion
                                disableGutters
                                elevation={0}
                                sx={{ borderRadius: 2, border: '1px solid #eceef1', '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box display="flex" alignItems="center" gap={0.75} sx={{ color: '#6b7280', lineHeight: 0 }}>
                                        <CodeOutlinedIcon sx={{ fontSize: 17 }} />
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', lineHeight: 1, m: 0 }}>
                                            Raw Payload (JSON)
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, display: 'block' }}>
                                    <Box
                                        component="pre"
                                        sx={{
                                            fontFamily: "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace",
                                            fontSize: '0.78rem', lineHeight: 1.6, backgroundColor: '#1e2430', color: '#d4d8dd',
                                            padding: '14px 16px', borderRadius: 1, overflowX: 'auto', maxHeight: 320,
                                            margin: 0, textAlign: 'left', whiteSpace: 'pre',
                                        }}
                                    >
                                        {(() => {
                                            try {
                                                const parsed = JSON.parse(detail.rawPayloadJson || '{}');
                                                return JSON.stringify(parsed, null, 2)
                                                    .split('\n')
                                                    .map((line, i) => {
                                                        const match = line.match(/^(\s*)"([^"]+)"(\s*:\s*)(.*)$/);
                                                        if (!match) return <div key={i}>{line}</div>;
                                                        const [, indent, key, colon, rest] = match;
                                                        return (
                                                            <div key={i}>
                                                                {indent}
                                                                <span style={{ color: '#7ec3ff' }}>"{key}"</span>
                                                                {colon}
                                                                <span style={{ color: '#c9d97a' }}>{rest}</span>
                                                            </div>
                                                        );
                                                    });
                                            } catch {
                                                return detail.rawPayloadJson;
                                            }
                                        })()}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    </Box>
                )}
                {!loading && !detail && (
                    <Typography variant="body2" color="textSecondary">No record found.</Typography>
                )}
            </Box>
        </Box>
    );
};

export default RecordDetailDrawer;
